import React, { useEffect } from "react";
import { SignInForm } from "@core/components/auth/SignInForm";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function AdminLoginPage() {
  const { t } = useTranslation("forms");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const from = (location.state as any)?.from?.pathname || "/admin";
  // Mode admin uniquement si l'utilisateur a été redirigé depuis une route /admin*
  // (sinon on affiche un écran de connexion générique pour ne pas divulguer
  // l'existence du panel support à un visiteur qui arrive juste sur /login).
  const isAdminContext = from.startsWith("/admin");

  // Google OAuth lit `auth_return_to` pour construire redirectTo et peut
  // contenir un reliquat d'un flow précédent (ex: "/"). On force la valeur
  // à `from` (généralement "/admin") pour que /auth/callback renvoie ici.
  useEffect(() => {
    localStorage.setItem("auth_return_to", from);
    return () => {
      if (localStorage.getItem("auth_return_to") === from) {
        localStorage.removeItem("auth_return_to");
      }
    };
  }, [from]);

  // Redirect once the auth state has actually propagated.
  // Navigating synchronously from SignInForm.onSuccess causes a race:
  // AdminGuard would render before AuthContext.user is populated and
  // bounce the user straight back to /login.
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSuccess = () => {
    // no-op: navigation is driven by the useEffect above once `user` is set.
  };

  return (
    <div className="min-h-screen-safe bg-[#030712] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            {isAdminContext
              ? t("auth.admin.titleAdmin")
              : t("auth.admin.titleGeneric")}
          </h1>
          <p className="text-gray-400">
            {isAdminContext
              ? t("auth.admin.subtitleAdmin")
              : t("auth.admin.subtitleGeneric")}
          </p>
        </div>

        <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl p-1 shadow-2xl">
          <SignInForm
            onSuccess={handleSuccess}
            onSwitchToSignUp={() => {
              // Optionnel: on peut désactiver l'inscription pour l'admin
            }}
          />
        </div>

        <button
          onClick={() => navigate("/")}
          className="w-full text-center text-sm text-gray-500 hover:text-white transition-colors"
        >
          {t("common.backToHome")}
        </button>
      </div>
    </div>
  );
}
